import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  OnInit,
  computed,
  input,
} from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { NavigationHistoryService } from '@core/services/navigation-history/navigation-history.service';
import {
  IContentBuilderFieldColor,
  IContentBuilderFieldImage,
} from '@core/models/content-builder-fields.model';
import { Meta } from '@angular/platform-browser';
import { RouterHelperService } from '@core/services/router-helper/router-helper.service';
import { ColorFieldTypesValues } from '@config/values/color-field-types.values';
import { AuthStoreSelector } from '@common/selectors/authentication.selector';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { AnalyticsService } from '@core/services/app/analytics.service';
import { PageHistoryService } from '@core/services/navigation-history/page-history.service';

@Component({
  selector: 'app-kiosk-splash-screen-page',
  templateUrl: './kiosk-splash-screen-page.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  styleUrls: ['./kiosk-splash-screen-page.component.scss'],
})
export class KioskSplashScreenPageComponent implements OnInit {
  readonly body = input<any>();
  readonly footerKiosk = input<any>();

  readonly title = input<string>();
  readonly labelCallToAction = input<string>();
  readonly linkUrl = input.required<string>();
  readonly displayAccessibilityButton = input<boolean>(true);
  readonly labelAccessibilityButton = input<string>();
  readonly backgroundColor = input<IContentBuilderFieldColor | undefined>({
    value: 'primary-800',
    type: ColorFieldTypesValues.Palette,
  });
  readonly backgroundImage = input<IContentBuilderFieldImage | undefined>();
  readonly backgroundVideo = input<IContentBuilderFieldImage | undefined>();
  readonly textColor = input<IContentBuilderFieldColor | undefined>({
    value: '#FFFFFF',
    type: ColorFieldTypesValues.Hex,
  });

  readonly backgroundOpacity = input<number | undefined>(1);

  readonly metaDescription = input<string>();

  protected readonly isVideoMedia = computed(() => {
    return !!this.backgroundVideo();
  });

  constructor(
    private readonly activatedRoute: ActivatedRoute,
    private readonly navigationHistoryService: NavigationHistoryService,
    private el: ElementRef,
    private readonly meta: Meta,
    private router: Router,
    private helperRouter: RouterHelperService,
    private readonly authStoreSelector: AuthStoreSelector,
    private readonly analyticsService: AnalyticsService,
    private readonly pageHistoryService: PageHistoryService,
  ) {
    this.authStoreSelector.selectUser.pipe(takeUntilDestroyed()).subscribe(user => {
      if (user?.metafields && user.metafields.length) {
        const historyPages = this.pageHistoryService.getHistory();
        this.analyticsService.log({
          level: 'error',
          label: '#DOOD-1161 User connected on kiosk splash screen (must not happen) -',
          message: {
            user,
            route: window.location.href,
            historyPages,
            warn: 'make sure this splash screen is the home page',
          },
        });
      }
    });
  }

  ngOnInit(): void {
    this.navigationHistoryService.pushStateFromActivatedRouteSnapshot(
      '/' + this.activatedRoute.snapshot.url.join('/'),
      this.activatedRoute.snapshot,
    );

    if (this.textColor()?.value) {
      this.setTextOverrideColor();
    }
    const metaDescription = this.metaDescription();
    if (metaDescription) {
      this.meta.updateTag({
        name: 'description',
        content: metaDescription,
      });
    }
  }

  setTextOverrideColor(): void {
    const textColor = this.textColor()?.value;
    this.el.nativeElement.style.setProperty('--my-class-bg-color', textColor);
  }

  redirect(): void {
    const linkUrl = this.linkUrl();
    this.analyticsService.log({
      level: 'debug',
      label: '#DOOD-1250 Navigate',
      message: {
        from: 'KioskSplashScreenPageComponent Ligne°101',
        target: [this.helperRouter.translateRoute(linkUrl)],
        currentLocation: window.location.pathname,
      },
    });
    this.router.navigate([this.helperRouter.translateRoute(linkUrl)]);
  }

  onInnerClick(event: Event): void {
    event.stopPropagation();
  }
}
